import { type RefObject, useEffect } from 'react'
import { useCallbackEvent } from './use-callback-event'

type Event = MouseEvent | TouchEvent
type Ref = RefObject<HTMLElement | null> | null

const isInsideDOM = (element: HTMLElement, event: Event): boolean => {
  if (!element) return false

  if (event.composedPath) {
    return event.composedPath().includes(element)
  }
  else {
    return element.contains(event.target as Node)
  }
}

export interface UseOnClickAwayProps {
  ref: Ref | Ref[]
  callback?: (e?: Event) => void
  mouseEvent?: 'click' | 'mousedown' | 'mouseup'
  touchEvent?: 'touchstart' | 'touchend' | 'pointerdown' | 'pointerup'
}

export const useOnClickAway = (props: UseOnClickAwayProps) => {
  const { ref, callback = () => { /* no-op */ }, mouseEvent = 'mousedown', touchEvent = 'touchend' } = props
  const clickCallback = useCallbackEvent(callback)

  useEffect(() => {
    const handleClickAway = (event: Event) => {
      if (Array.isArray(ref)) {
        const someElementIsInside = ref
          .filter(singleRef => singleRef !== null)
          .some((singleRef) => {
            if (!singleRef.current) return false
            return isInsideDOM(singleRef.current, event)
          })
        if (!someElementIsInside) {
          clickCallback(event)
        }
      }
      else {
        if (!ref?.current) {
          return
        }
        if (!isInsideDOM(ref.current, event)) {
          clickCallback(event)
        }
      }
    }
    document.addEventListener(mouseEvent, handleClickAway)
    document.addEventListener(touchEvent, handleClickAway)

    return () => {
      document.removeEventListener(mouseEvent, handleClickAway)
      document.removeEventListener(touchEvent, handleClickAway)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, mouseEvent, touchEvent])
}
